import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Paper,
	Skeleton,
	Typography,
} from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	addGpsrProductTypeRequest,
	addGpsrRegulationsRequest,
	activateGpsrRequest,
	cancelGpsrRequest,
	deleteGpsrProductTypeRequest,
	deleteGpsrRegulationsRequest,
	generateInvoiceGpsrRequest,
	getDocFileRequest,
	getGpsrProductTypesRequest,
	getGpsrRegulationsRequest,
	getTechFileRequest,
	downloadGpsrLastInvoiceRequest,
	updateGpsrServiceSettingsRequest,
	updateGpsrRemoveOnRenewalRequest,
	changeGpsrStatusRequest,
	getGpsrServiceStatusesRequest,
} from 'redux/reducers/gpsr/reducer';
import { PAGE_SIZE } from 'services/constants/env';
import { EasTableFilterPanel } from 'ui/EasTableFilterPanel';
import { EasTable } from 'ui/EasTable';
import {
	TAddGpsrRegulationsBodyRequest,
	TGpsrItem,
	TGpsrProductType,
	TGpsrRegulationsItem,
	EServiceStatus,
	SERVICE_STATUSES,
	EServiceStatusID,
} from 'redux/reducers/gpsr/types';
import {
	getGpsrDocFileLoad,
	getGpsrProductTypes,
	getGpsrProductTypesLoad,
	getGpsrRegulations,
	getGpsrRegulationsLoad,
	getGpsrTechFileLoad,
	getRegisterError,
	getDownloadGpsrLastInvoiceLoading,
	getUpdateGpsrServiceSettingsLoading,
	getGpsrServiceStatuses,
} from 'redux/reducers/gpsr/selectors';
import { useLocation, useNavigate, useParams } from 'react-router';
import queryString from 'query-string';
import { PaginationBlock } from 'ui/PaginationBlock';
import { TFilters } from 'redux/types';
import { DATES_ARRAY } from 'services/constants/dates-constants';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { Block, CheckCircle, KeyboardBackspace } from '@mui/icons-material';
import { TSetParameters } from 'ui/AddParametersDialog/types';
import { DeleteDialog } from 'ui/DeleteDialog';
import { TTableStructure } from 'ui/EasTable/types';
import { PATHS } from 'routes/constants';
import { getSavedUrl, getSavedUrl2 } from 'redux/reducers/auth/selectors';
import { getUsersList } from 'redux/reducers/user/selectors';
import { TUser } from 'redux/reducers/user/types';
import { gpsrTableStructure } from 'components/Gpsr/data/gpsr-table-structure';
import { saveUrl, saveUrl2 } from 'redux/reducers/auth/reducer';
import { gpsrProductTypeTableStructure } from './data/gpsr-product-type-table-structure';
import { gpsrRegulationsTableStructure } from './data/gpsr-regulations-table-structure';
import { AddProductTypeDialog } from './AddProductTypeDialog';
import { AddRegulationsDialog } from './AddRegulationsDialog';
import { GpsrEditCustomerDialog } from './GpsrEditCustomerDialog';
import { EasFullDataComponent } from '../../ui/EasFullDataComponent';
import { GpsrInvoicesListDialog } from './GpsrInvoicesListDialog';
import { ChangeSubscriptionInformationDialog } from './ChangeSubscriptionInformationDialog';
import { ExtendSubscriptionDialog } from './ExtendSubscriptionDialog';
import { EasPagination } from '../../ui/EasPagination';

export const GpsrProductTypes: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const params = useParams<{ emId?: string; productTypeId?: string }>();

	const { page } = queryString.parse(search);

	const prevPageRef = useRef(page);
	const prevSearchRef = useRef(search);

	const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
	const [openAddProductTypeDialog, setOpenAddProductTypeDialog] = useState(false);
	const [openAddRegulationsDialog, setOpenAddRegulationsDialog] = useState(false);
	const [openDeleteProductTypeDialog, setOpenDeleteProductTypeDialog] = useState(false);
	const [openDeleteRegulationsDialog, setOpenDeleteRegulationsDialog] = useState(false);
	const [openEditCustomerDialog, setOpenEditCustomerDialog] = useState(false);
	const [openMarkForRemovalDialog, setOpenMarkForRemovalDialog] = useState(false);
	const [selectedProductType, setSelectedProductType] = useState<number | null>(null);
	const [selectedRegulations, setSelectedRegulations] = useState<number | null>(null);
	const [openChangeSubscriptionInformationDialog, setOpenChangeSubscriptionInformationDialog] =
		useState(false);
	const [openExtendSubscriptionDialog, setOpenExtendSubscriptionDialog] = useState(false);

	const savedUrl = useSelector(getSavedUrl);
	const savedUrl2 = useSelector(getSavedUrl2);

	const items = useSelector(getGpsrProductTypes);
	const itemsIsLoading = useSelector(getGpsrProductTypesLoad);

	const regulations = useSelector(getGpsrRegulations);
	const regulationsIsLoading = useSelector(getGpsrRegulationsLoad);
	const usersList = useSelector(getUsersList) as TUser[];
	const serviceStatuses = useSelector(getGpsrServiceStatuses);
	const tableStructure: TTableStructure<keyof TGpsrProductType>[] = gpsrProductTypeTableStructure;
	const gpsrItemTableStructure: TTableStructure<keyof TGpsrItem>[] = gpsrTableStructure({
		usersList,
		serviceStatuses: serviceStatuses ?? [],
	});
	const regulationsTableStructure: TTableStructure<keyof TGpsrRegulationsItem>[] =
		gpsrRegulationsTableStructure;

	const techFileIsLoading = useSelector(getGpsrTechFileLoad);

	const docFileIsLoading = useSelector(getGpsrDocFileLoad);

	const downloadGpsrLastInvoiceLoading = useSelector(getDownloadGpsrLastInvoiceLoading);

	const updateGpsrServiceSettingsLoading = useSelector(getUpdateGpsrServiceSettingsLoading);

	const [openStopNotificationsDialog, setOpenStopNotificationsDialog] = useState(false);

	const handleStopNotifications = () => {
		if (!items?.merchant?.id) return;
		dispatch(updateGpsrServiceSettingsRequest({ stopNotifications: true, id: items.merchant.id }));
		setOpenStopNotificationsDialog(false);
	};

	const setCloseInvoiceDialog = () => {
		setOpenInvoiceDialog(false);
	};

	const handleOpenInvoiceDialog = () => {
		setOpenInvoiceDialog(true);
	};

	const handleConfirmInvoiceDialog = () => {
		if (!items?.merchant?.id) return;
		dispatch(generateInvoiceGpsrRequest({ invoiceId: items.merchant.id }));
		setCloseInvoiceDialog();
	};

	const handleOpenAddProductTypeDialog = () => {
		setOpenAddProductTypeDialog(true);
	};

	const handleOpenAddRegulationsDialog = () => {
		setOpenAddRegulationsDialog(true);
	};

	const handleOpenDeleteProductTypeDialog = () => {
		setOpenDeleteProductTypeDialog(true);
	};

	const handleOpenDeleteRegulationsDialog = () => {
		setOpenDeleteRegulationsDialog(true);
	};

	const handleCloseAddProductTypeDialog = () => {
		setOpenAddProductTypeDialog(false);
	};

	const handleCloseAddRegulationsDialog = () => {
		setOpenAddRegulationsDialog(false);
	};

	const handleCloseDeleteProductTypeDialog = () => {
		setOpenDeleteProductTypeDialog(false);
	};

	const handleCloseDeleteRegulationsDialog = () => {
		setOpenDeleteRegulationsDialog(false);
	};

	const handleOpenEditCustomerDialog = () => {
		setOpenEditCustomerDialog(true);
	};

	const handleCloseEditCustomerDialog = () => {
		setOpenEditCustomerDialog(false);
	};

	const handleOpenMarkForRemovalDialog = () => {
		setOpenMarkForRemovalDialog(true);
	};

	const handleCloseMarkForRemovalDialog = () => {
		setOpenMarkForRemovalDialog(false);
	};

	const handleOpenChangeSubscriptionInformationDialog = () => {
		setOpenChangeSubscriptionInformationDialog(true);
	};

	const handleCloseChangeSubscriptionInformationDialog = () => {
		setOpenChangeSubscriptionInformationDialog(false);
	};

	const handleEditCustomer = () => {
		setOpenEditCustomerDialog(false);
	};

	const handleAddProductType = (data: TSetParameters[]) => {
		if (!params.emId) return;
		dispatch(
			addGpsrProductTypeRequest({
				emId: +params.emId,
				body: {
					id: null,
					name: data[0].value,
					description: data[1].value,
					productTypeId: data[2].value ? +data[2].value : null,
				},
				request: {
					emId: +params.emId,
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
				},
			}),
		);
		handleCloseAddProductTypeDialog();
	};

	const handleAddRegulations = (data: TAddGpsrRegulationsBodyRequest) => {
		if (!params.emId) return;
		if (!params.productTypeId) return;
		dispatch(
			addGpsrRegulationsRequest({
				emId: +params.emId,
				productTypeId: +params.productTypeId,
				body: data,
			}),
		);
		handleCloseAddRegulationsDialog();
	};

	const handleDeleteProductType = () => {
		if (!params.emId) return;
		if (!selectedProductType) return;
		dispatch(
			deleteGpsrProductTypeRequest({
				emId: +params.emId,
				productTypeId: selectedProductType,
				request: {
					emId: +params.emId,
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
				},
			}),
		);
		setSelectedProductType(null);
		handleCloseDeleteProductTypeDialog();
	};

	const handleDeleteRegulations = () => {
		if (!params.emId) return;
		if (!params.productTypeId) return;
		// if (!selectedProductType) return;
		if (!selectedRegulations) return;
		dispatch(
			deleteGpsrRegulationsRequest({
				emId: +params.emId,
				productTypeId: +params.productTypeId,
				regulationId: selectedRegulations,
			}),
		);
		setSelectedProductType(null);
		setSelectedRegulations(null);
		handleCloseDeleteRegulationsDialog();
	};

	// useEffect(() => {
	// 	dispatch(
	// 		getGpsrItemsRequest({
	// 			pageSize: PAGE_SIZE,
	// 			searchString: '',
	// 			pageNumber: 1,
	// 		}),
	// 	);
	// }, [dispatch]);

	// 		dispatch(
	// 			getMtdReportsRequest({
	// 				pageNumber: page ? +page : 1,
	// 				pageSize: PAGE_SIZE,
	// 				filters,
	// 				searchString: String(navigateParams?.search ?? ''),
	// 				orderBy: [
	// 					{
	// 						fieldName: navigateParams?.sortIndex
	// 							? tableStructure[+navigateParams.sortIndex].selector
	// 							: 'id',
	// 						direction: navigateParams?.sortDirection
	// 							? String(navigateParams.sortDirection) === 'desc'
	// 								? 'desc'
	// 								: 'asc'
	// 							: 'desc',
	// 					},
	// 				],
	// 			}),
	// 		);
	// 		// eslint-disable-next-line react-hooks/exhaustive-deps
	// 	}, [dispatch, location.search, params?.level1]);

	const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
		if (!!navigateParams[item.selector] && item.type === 'date') {
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
			// console.log('dates', dates);
			if (dates?.from) {
				acc.push({
					fieldName: item.selector,
					value: dates.from,
					operator: '>=',
				});
			}
			if (dates?.to) {
				acc.push({
					fieldName: item.selector,
					value: dates.to,
					operator: '<=',
				});
			}
			return acc;
		}
		// if (
		// 	navigateParams[item.selector] &&
		// 	item.selector === 'status' &&
		// 	navigateParams.status === 'Not Fixed'
		// ) {
		// 	acc.push({
		// 		fieldName: 'status',
		// 		value: 'Fixed',
		// 		operator: '<>',
		// 	});
		// }
		if (navigateParams[item.selector])
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilters);

	useEffect(() => {
		if (!params.emId) return;
		const prevPage = prevPageRef.current;
		const prevSearch = prevSearchRef.current;

		if (page === prevPage && search === prevSearch) {
			prevPageRef.current = page;
			return;
		}

		const newPath = `/gpsr/${params.emId}`;
		navigate(`${newPath}${search}`, { replace: true });
		prevPageRef.current = page;
		prevSearchRef.current = search;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [params.emId, params.productTypeId, search]);

	useEffect(() => {
		if (!params.emId) return;
		if (params.productTypeId) {
			dispatch(
				getGpsrRegulationsRequest({
					emId: +params.emId,
					productTypeId: +params.productTypeId,
				}),
			);
		}
		dispatch(
			getGpsrProductTypesRequest({
				emId: +params.emId,
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, params.emId, params.productTypeId, search]);

	// useEffect(() => {
	// 	if (!params.emId) return;
	// 	if (!params.productTypeId) return;
	// 	setSelectedProductType(null);
	// 	setSelectedRegulations(null);

	// 	navigate(
	// 		`/gpsr/${params.emId}?${queryString.stringify({
	// 			...navigateParams,
	// 		})}`,
	// 	);
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [search]);

	const handleReplayHistory = () => {
		// window.history.back();
		if (savedUrl) {
			console.log('=========savedUrl', savedUrl);
			console.log('=========savedUrl2', savedUrl2);
			if (savedUrl.includes(PATHS.GPSR_PAGE) && !savedUrl.includes(`${PATHS.GPSR_PAGE}/`)) {
				navigate(savedUrl);
			} else if (
				savedUrl2 &&
				savedUrl2.includes(PATHS.GPSR_PAGE) &&
				!savedUrl2.includes(`${PATHS.GPSR_PAGE}/`)
			) {
				navigate(savedUrl2);
			} else navigate(PATHS.GPSR_PAGE);
		}
	};

	const handleRegulationsLink = (productTypeId: number) => {
		setSelectedProductType(productTypeId);
		if (!params.emId) return;
		navigate(
			`/gpsr/${params.emId}/${productTypeId}?${queryString.stringify({
				...navigateParams,
			})}`,
		);
	};

	const handleDownloadDocFile = (productTypeId: number) => {
		console.log('handleDownloadDocFile', productTypeId);
		if (!params.emId) return;
		dispatch(getDocFileRequest({ emId: +params.emId, productTypeId }));
	};

	const handleDownloadTechFile = (productTypeId: number) => {
		if (!params.emId) return;
		dispatch(getTechFileRequest({ emId: +params.emId, productTypeId }));
	};

	const handleMarkForRemoval = (productTypeId: number) => {
		if (!params.emId) return;
		dispatch(
			updateGpsrRemoveOnRenewalRequest({
				emId: +params.emId,
				id: productTypeId,
				removeOnRenewal: true,
			}),
		);
		handleCloseMarkForRemovalDialog();
	};

	const handleActionProductType = (productTypeId: number, type: string) => {
		setSelectedProductType(productTypeId);
		if (type === 'Delete') {
			handleOpenDeleteProductTypeDialog();
		}
		if (type === 'Edit') {
			setOpenAddProductTypeDialog(true);
		}
		if (type === 'Tech File') {
			handleDownloadTechFile(productTypeId);
		}
		if (type === 'DoC File') {
			handleDownloadDocFile(productTypeId);
		}
		if (type === 'Mark for Removal') {
			handleOpenMarkForRemovalDialog();
		}
	};

	const handleActionRegulations = (regulationsId: number, type: string) => {
		console.log('handleActionRegulations', regulationsId, type);
		setSelectedRegulations(regulationsId);
		if (type === 'Delete') {
			handleOpenDeleteRegulationsDialog();
		}
		if (type === 'Edit') {
			setOpenAddRegulationsDialog(true);
		}
	};

	const handleDownloadLastInvoice = () => {
		if (!params.emId) return;
		dispatch(downloadGpsrLastInvoiceRequest({ id: items?.merchant?.id ?? 0 }));
	};
	const [openInvoicesListDialog, setOpenInvoicesListDialog] = useState(false);

	const handleShowInvoicesList = () => {
		if (!params.emId) return;
		if (
			savedUrl &&
			savedUrl.includes(PATHS.GPSR_PAGE) &&
			!savedUrl.includes(`${PATHS.GPSR_PAGE}/`)
		) {
			dispatch(saveUrl2(savedUrl));
		}
		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(`/gpsr/${params.emId}/invoices`);
	};

	const [openCancelationDialog, setOpenCancelationDialog] = useState(false);

	const handleCancelGpsr = () => {
		if (!params.emId) return;
		dispatch(
			cancelGpsrRequest({
				id: items?.merchant?.id ?? 0,
				request: {
					emId: +params.emId,
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
				},
			}),
		);
		setOpenCancelationDialog(false);
	};

	const [openActivateGpsrDialog, setOpenActivateGpsrDialog] = useState(false);

	const handleActivateGpsr = () => {
		if (!params.emId) return;
		dispatch(
			activateGpsrRequest({
				id: items?.merchant?.id ?? 0,
				request: {
					emId: +params.emId,
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
				},
			}),
		);
		setOpenActivateGpsrDialog(false);
	};

	const handleChangeGpsrStatus = () => {
		console.log('handleChangeGpsrStatus', items?.merchant?.serviceStatusId);
		if (!params.emId) return;
		dispatch(
			changeGpsrStatusRequest({
				id: items?.merchant?.id ?? 0,
				statusId:
					items?.merchant?.serviceStatusId === EServiceStatusID.DISABLED
						? EServiceStatusID.REGISTRATION
						: EServiceStatusID.DISABLED,
				request: {
					emId: +params.emId,
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
				},
			}),
		);
	};

	useEffect(() => {
		if (!serviceStatuses) {
			dispatch(getGpsrServiceStatusesRequest());
		}
	}, [dispatch, serviceStatuses]);

	return (
		<Box>
			{items?.merchant && (
				<>
					{/* <EasTable<TGpsrItem>
							tableStructure={gpsrItemTableStructure}
							items={items?.merchant ? [items?.merchant] : []}
							itemsIsLoading={itemsIsLoading}
							handleAction={() => {}}
							handleLink={() => {}}
							skeletonRows={[1]}
						/> */}

					<EasFullDataComponent<TGpsrItem>
						tableStructure={gpsrItemTableStructure}
						data={items?.merchant ?? null}
						itemsIsLoading={itemsIsLoading}
						header={
							<Box
								sx={{
									backgroundColor: 'primary.light',
									p: '4px 4px 4px 8px',
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									gap: '10px',
									width: '100%',
								}}
							>
								<Box
									sx={{
										backgroundColor: 'primary.light',
										display: 'flex',
										justifyContent: 'start',
										alignItems: 'center',
										gap: '10px',
										cursor: 'pointer',
									}}
									onClick={handleReplayHistory}
								>
									<IconButton
										color="primary"
										sx={{
											p: '0',
										}}
									>
										<KeyboardBackspace />
									</IconButton>
									<Typography
										sx={{
											fontSize: '.9rem',
											color: 'primary.main',
											fontWeight: 'bold',
										}}
									>
										GPSR
									</Typography>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', ml: '10px' }}>
										<Typography
											sx={{
												fontSize: '.8rem',
												color: 'primary.main',
											}}
										>
											Service Status:
										</Typography>
										<Chip
											label={
												serviceStatuses?.find(
													(status) => status.id === items?.merchant?.serviceStatusId,
												)?.displayName || ''
											}
											size="small"
											sx={{
												fontSize: '.7rem',
												color: '#00000080',
												height: '20px',
												fontWeight: 'bold',
												backgroundColor:
													SERVICE_STATUSES.find(
														(i) => i.name === items?.merchant?.serviceStatusName,
													)?.color ?? 'primary.main',
											}}
										/>
									</Box>
								</Box>
								<Box
									sx={{
										backgroundColor: 'primary.light',
										display: 'flex',
										justifyContent: 'start',
										alignItems: 'center',
										gap: '4px',
										cursor: 'pointer',
									}}
								>
									{items?.merchant?.serviceStatusName === EServiceStatus.DISABLED ? (
										<EasTooltip title="Enable Service">
											<IconButton
												color="primary"
												sx={{
													p: '0',
												}}
												onClick={handleChangeGpsrStatus}
											>
												<CheckCircle color="success" fontSize="large" />
											</IconButton>
										</EasTooltip>
									) : (
										<EasTooltip title="Disable Service">
											<IconButton
												color="primary"
												sx={{
													p: '0',
												}}
												onClick={handleChangeGpsrStatus}
											>
												<Block color="error" fontSize="large" />
											</IconButton>
										</EasTooltip>
									)}
									<Button
										variant="contained"
										size="small"
										onClick={() => {
											handleShowInvoicesList();
										}}
									>
										Show Invoices List
									</Button>

									<Button
										type="button"
										variant="contained"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleDownloadLastInvoice();
										}}
										disabled={downloadGpsrLastInvoiceLoading}
									>
										Download Last Invoice
										{downloadGpsrLastInvoiceLoading && (
											<Box sx={{ position: 'absolute', right: '0', top: '0', width: '100%' }}>
												<CircularProgress size={20} />
											</Box>
										)}
									</Button>
									<Button
										type="button"
										variant="contained"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											setSelectedProductType(null);
											setSelectedRegulations(null);
											handleOpenEditCustomerDialog();
										}}
									>
										Edit Customer
									</Button>
									{!items?.merchant?.invoiceId && (
										<Button
											type="button"
											variant="contained"
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												setSelectedProductType(null);
												setSelectedRegulations(null);
												handleOpenInvoiceDialog();
											}}
											disabled={!!items?.merchant?.invoiceId}
										>
											Generate Invoice
										</Button>
									)}
									{items?.merchant?.serviceStatusName !== EServiceStatus.CANCELLED && (
										<Button
											type="button"
											variant="contained"
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												setOpenCancelationDialog(true);
											}}
										>
											Disable Auto-renewal
										</Button>
									)}
									{items?.merchant?.serviceStatusName === EServiceStatus.CANCELLED && (
										<Button
											type="button"
											variant="contained"
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												setOpenActivateGpsrDialog(true);
											}}
										>
											Activate auto-renewal
										</Button>
									)}
									{items?.merchant?.allowEditing && (
										<>
											<Button
												type="button"
												variant="contained"
												size="small"
												disabled={updateGpsrServiceSettingsLoading}
												onClick={(e) => {
													e.stopPropagation();
													setOpenStopNotificationsDialog(true);
												}}
											>
												Stop Notifications
											</Button>
											<Button
												type="button"
												variant="contained"
												size="small"
												onClick={handleOpenChangeSubscriptionInformationDialog}
											>
												Change Subscription Information
											</Button>
										</>
									)}
									<Button
										type="button"
										variant="contained"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											setOpenExtendSubscriptionDialog(true);
										}}
									>
										Extend Subscription
									</Button>
								</Box>
							</Box>
						}
					/>
				</>
			)}
			{!items?.merchant &&
				itemsIsLoading &&
				new Array(4)
					.fill(0)
					.map((i) => <Skeleton key={i} variant="rectangular" height="25px" sx={{ mb: '25px' }} />)}

			<Box sx={{ margin: '0px 10px 0 10px' }}>
				<Grid container spacing={1.25}>
					<Grid item xs={12} lg={6}>
						<Paper
							sx={{
								// margin: '0px 10px 0 10px',
								// maxWidth: 'calc(100vw - 20px)',
								overflowX: 'auto',
							}}
						>
							<Box sx={{ width: 'auto', minWidth: '600px' }}>
								<Box sx={{ borderRadius: '12px' }}>
									<Box
										sx={{
											backgroundColor: 'primary.light',
											p: 0.5,
											display: 'flex',
										}}
									>
										<Typography
											sx={{
												fontSize: '.9rem',
												color: 'primary.main',
												fontWeight: 'bold',
											}}
										>
											GPSR Product Types
										</Typography>
									</Box>
									<EasTableFilterPanel
										tableStructure={tableStructure}
										loading={
											itemsIsLoading ||
											regulationsIsLoading ||
											techFileIsLoading ||
											docFileIsLoading
										}
									>
										<Box sx={{ display: 'flex', gap: '10px' }}>
											<Button
												type="button"
												variant="contained"
												size="small"
												onClick={() => {
													setSelectedProductType(null);
													setSelectedRegulations(null);
													handleOpenAddProductTypeDialog();
												}}
											>
												Add Product Type
											</Button>
										</Box>
									</EasTableFilterPanel>

									<EasTable<TGpsrProductType>
										tableStructure={tableStructure}
										items={items?.data ?? []}
										itemsIsLoading={itemsIsLoading || regulationsIsLoading}
										handleAction={handleActionProductType}
										handleLink={handleRegulationsLink}
										skeletonRows={[1, 2]}
										highlightedRow={
											items?.data.findIndex((i) => i.id === +(params?.productTypeId ?? 0)) ?? null
										}
									/>
								</Box>
							</Box>
						</Paper>
						<Box
							sx={{
								padding: '0 20px 0 20px',
								display: 'flex',
								justifyContent: 'space-between',
							}}
						>
							<EasPagination total={items?.totalCount ?? 0} isLoading={itemsIsLoading} />
						</Box>
					</Grid>
					<Grid item xs={12} lg={6}>
						{params.productTypeId ? (
							<Paper
								sx={{
									// margin: '0px 10px 10px 10px',
									maxWidth: 'calc(100vw - 20px)',
									overflowX: 'auto',
								}}
							>
								<Box sx={{ width: 'auto', minWidth: '600px' }}>
									<Box sx={{ borderRadius: '12px' }}>
										<Box
											sx={{
												backgroundColor: 'primary.light',
												p: 0.5,
												display: 'flex',
											}}
										>
											<Typography
												sx={{
													fontSize: '.9rem',
													color: 'primary.main',
													fontWeight: 'bold',
												}}
											>
												GPSR Regulations for Product Type{' '}
												<i>
													{items?.data.find((i) => i.id === +(params?.productTypeId ?? 0))?.name ??
														''}
												</i>
											</Typography>
										</Box>
										<EasTableFilterPanel
											tableStructure={regulationsTableStructure}
											loading={itemsIsLoading || regulationsIsLoading}
										>
											<Button
												type="button"
												variant="contained"
												size="small"
												onClick={() => {
													setSelectedProductType(null);
													setSelectedRegulations(null);
													handleOpenAddRegulationsDialog();
												}}
											>
												Add Regulations
											</Button>
										</EasTableFilterPanel>

										<EasTable<TGpsrRegulationsItem>
											tableStructure={regulationsTableStructure}
											items={regulations ?? []}
											itemsIsLoading={itemsIsLoading || regulationsIsLoading}
											handleAction={handleActionRegulations}
											handleLink={() => {}}
											skeletonRows={[1, 2]}
										/>
									</Box>
								</Box>
							</Paper>
						) : (
							<Box
								sx={{
									p: '35px',
									display: 'flex',
									justifyContent: 'center',
								}}
							>
								<Typography color="primary" sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>
									Select Product Type to see Regulations
								</Typography>
							</Box>
						)}
					</Grid>
				</Grid>
			</Box>

			<ConfirmationDialog
				open={openInvoiceDialog}
				setClose={setCloseInvoiceDialog}
				handleConfirm={handleConfirmInvoiceDialog}
				title="Generate Invoice"
				text="<b>Are you sure you want to generate Invoice?</b><br />
Please check that proper GPSR plan selected and additional product types number set as agreed with the customer."
			/>
			{/* <AddParametersDialog
				open={openAddProductTypeDialog}
				setClose={handleCloseAddProductTypeDialog}
				handleConfirm={handleAddProductType}
				parameters={[
					{
						name: 'name',
						value: '',
						type: 'string',
						label: 'Name',
						required: true,
					},
					{
						name: 'description',
						value: '',
						type: 'string',
						label: 'Description',
						required: true,
					},
				]}
				title="Add Product Type"
			/> */}
			{/* <AddParametersDialog
				open={openAddRegulationsDialog}
				setClose={handleCloseAddRegulationsDialog}
				handleConfirm={handleAddRegulations}
				title="Add Regulations"
				tableStructure={regulationsTableStructure}
			/> */}
			<AddProductTypeDialog
				open={openAddProductTypeDialog}
				setClose={handleCloseAddProductTypeDialog}
				emId={+(params?.emId ?? 0)}
				productTypeId={selectedProductType ?? null}
				productTypeNameProps={items?.data.find((i) => i.id === selectedProductType)?.name ?? ''}
				productTypeDescriptionProps={
					items?.data.find((i) => i.id === selectedProductType)?.description ?? ''
				}
				productTypeIdProps={
					items?.data.find((i) => i.id === selectedProductType)?.productTypeId ?? null
				}
			/>
			<AddRegulationsDialog
				open={openAddRegulationsDialog}
				setClose={handleCloseAddRegulationsDialog}
				emId={+(params?.emId ?? 0)}
				productTypeId={+(params?.productTypeId ?? 0)}
				regulationsId={selectedRegulations ?? null}
				regulationsNameProps={regulations?.find((i) => i.id === selectedRegulations)?.name ?? ''}
				regulationsReferencesIdProps={
					regulations?.find((i) => i.id === selectedRegulations)?.regulationId ?? null
				}
			/>
			<DeleteDialog
				open={openDeleteProductTypeDialog}
				setClose={handleCloseDeleteProductTypeDialog}
				handleDelete={handleDeleteProductType}
				// title="Delete Product Type"
				text="Are you sure you want to delete Product Type?"
			/>
			<DeleteDialog
				open={openDeleteRegulationsDialog}
				setClose={handleCloseDeleteRegulationsDialog}
				handleDelete={handleDeleteRegulations}
				// title="Delete Regulations"
				text="Are you sure you want to delete Regulations?"
			/>
			<GpsrEditCustomerDialog
				open={openEditCustomerDialog}
				setClose={handleCloseEditCustomerDialog}
			/>
			<GpsrInvoicesListDialog
				open={openInvoicesListDialog}
				setClose={() => setOpenInvoicesListDialog(false)}
			/>
			<ConfirmationDialog
				open={openCancelationDialog}
				setClose={() => setOpenCancelationDialog(false)}
				handleConfirm={handleCancelGpsr}
				title="Disable auto-renewal of the Subscription"
				text="Are you sure you want to disable auto-renewal of your GPSR subscription?"
			/>
			<ConfirmationDialog
				open={openActivateGpsrDialog}
				setClose={() => setOpenActivateGpsrDialog(false)}
				handleConfirm={handleActivateGpsr}
				title="Activate auto-renewal of the Subscription"
				text="Are you sure you want to activate auto-renewal of your GPSR subscription?"
			/>
			<ConfirmationDialog
				open={openStopNotificationsDialog}
				setClose={() => setOpenStopNotificationsDialog(false)}
				handleConfirm={handleStopNotifications}
				title="Stop Notifications"
				text="Are you sure you want to stop notifications?"
			/>
			<ConfirmationDialog
				open={openMarkForRemovalDialog}
				setClose={handleCloseMarkForRemovalDialog}
				handleConfirm={() => handleMarkForRemoval(selectedProductType ?? 0)}
				title="Mark for Removal"
				text="Are you sure you want to mark for removal?"
			/>
			<ChangeSubscriptionInformationDialog
				open={openChangeSubscriptionInformationDialog}
				setClose={handleCloseChangeSubscriptionInformationDialog}
			/>
			{params.emId && openExtendSubscriptionDialog ? (
				<ExtendSubscriptionDialog
					setClose={() => setOpenExtendSubscriptionDialog(false)}
					currentEmId={+params.emId}
				/>
			) : null}
		</Box>
	);
};
